//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import type { IUnisonTheme } from '@bvengineering/unison-core';

/**
 * Echo design system slice of the Unison theme.
 *
 * Today this resolves to `theme.echo`. In the near future it will become the
 * top-level theme (Echo will replace the current `theme` surface), and may
 * also gain mode variants (light/dark/experimental). Components consume Echo
 * tokens exclusively through {@link getEchoTheme} and
 * {@link getEchoThemeComponents} so that the migration only changes these
 * helpers, not every call site.
 */
export type EchoTheme = IUnisonTheme['echo'];

/**
 * Map of Echo component token namespaces (e.g. `button`, `text`, `icon`).
 */
export type EchoComponents = EchoTheme['components'];

/**
 * Returns the Echo theme slice of a Unison theme object.
 *
 * Use this for primitive Echo tokens that are not scoped to a specific
 * component, e.g. `getEchoTheme(theme).color.text.onLight.default`,
 * `getEchoTheme(theme).spacing.md`, `getEchoTheme(theme).borderRadius.sm`.
 *
 * Prefer {@link getEchoThemeComponents} when reading component-scoped tokens.
 */
export const getEchoTheme = <T extends { echo: EchoTheme }>(
  theme: T,
): EchoTheme => theme.echo;

/**
 * Returns the Echo component token namespaces, fully typed.
 *
 * Each key is a component token namespace (`button`, `text`, `icon`, …).
 * Access the one you need via property access — autocomplete and
 * compile-time checks work exactly as with a keyed lookup.
 *
 * @example
 *   const { button } = getEchoThemeComponents(theme);
 *   button.primary.text.default; // autocompletes, type-checked
 */
export const getEchoThemeComponents = <
  T extends { echo: { components: EchoComponents } },
>(
  theme: T,
): EchoComponents => theme.echo.components;
